import React, {useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Redirect, useLocation} from 'react-router-dom'
import {Box, Container, Text} from '@chakra-ui/react'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {HTTPNotFound} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {API_ERROR_MESSAGE, HOME_HREF, SIGN_IN_HREF} from '../../constants'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import useMultiSite from '../../hooks/use-multi-site'
import useOcapiSite from '../../hooks/useOcapiSite'
import {isHydrated} from '../../utils/utils'
import {WARRANTY_MODES, WarrantyProvider} from './util/warranty-context'
import LoadingSpinner from '../../components/loading-spinner'
import {
    useCustomWarrantyConfiguration,
    useCustomWarrantyRegistrationMutation
} from '../../hooks/useScapiCustomApi'
import WarrantyWizard from './partials/warranty-wizard'
import WarrantyConfirmation from './partials/confirmation'
import logger from '../../utils/logger-instance'
import {useToast} from '../../hooks/use-toast'
import {useIntl} from 'react-intl'
import useEffectEvent from '../../hooks/use-effect-event'
import {useCountry} from '../../hooks'
import Seo from '../../components/seo'

/**
 * WarrantyPortal component
 * @param {'cpsia'|'warranty'} mode - mode of the warranty portal, either 'cpsia' or 'warranty'
 * @param {object} initialData - initial data for the warranty registration
 */
const WarrantyPortal = ({mode, initialData: _initialData}) => {
    const {formatMessage} = useIntl()
    const {cpsiaEnabled, isLoaded} = useOcapiSite()
    const location = useLocation()
    const {data: customer, isLoading: isCustomerLoading} = useCurrentCustomer()
    const {isRegistered, customerType} = customer
    const country = useCountry()
    const {buildUrl, site, locale} = useMultiSite()
    const preferredLanguage = locale?.id?.split('-')?.[0]
    const isLoading = customerType == null || !isHydrated()
    const toast = useToast()

    const defaultAddress = useMemo(
        () => (customer.addresses || []).find((address) => address.countryCode === country),
        [customer.addresses, country]
    )

    const initialDataWithCustomerInfo = {
        ..._initialData,
        firstName: _initialData?.firstName ?? customer?.firstName,
        lastName: _initialData?.lastName ?? customer?.lastName,
        personEmail: _initialData?.personEmail ?? customer?.email,
        phone: _initialData?.phone ?? customer?.phoneHome,
        billingStreet: _initialData?.billingStreet ?? defaultAddress?.address1,
        billingCity: _initialData?.billingCity ?? defaultAddress?.city,
        billingState: _initialData?.billingState ?? defaultAddress?.stateCode,
        billingPostalCode: _initialData?.billingPostalCode ?? defaultAddress?.postalCode
    }

    const {data: config, isLoading: isConfigLoading} = useCustomWarrantyConfiguration()

    // mutation to submit the registration data
    const {
        data: warrantyData,
        mutateAsync: submitWarrantyRegistration,
        isSuccess,
        isLoading: isSubmitting
    } = useCustomWarrantyRegistrationMutation({
        throwOnError: false
    })

    // handle submittion error
    const onError = useEffectEvent((error) => {
        logger.error('Error submitting warranty registration', error)
        toast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    })
    const onSubmit = useCallback(
        async (data) => {
            const {
                userInputSelectedProductGroup,
                userInputSelectedProductName,
                serialNumber,
                purchaseDate,
                userInputItemNumber,
                manufactureDate,
                userInputRetailerName,
                userInputStoreUrl,
                userInputRetailerCity,
                userInputRetailerCountry,
                firstName,
                lastName,
                personEmail,
                phone,
                billingStreet,
                billingCity,
                billingState,
                billingPostalCode
            } = data

            const selectedItemConfig = config.warranties.find(
                (item) => item.name === userInputSelectedProductName
            )

            const body = {
                siteId: site.id,
                locale: locale.id,
                customer: {
                    firstName,
                    lastName,
                    preferredLanguage,
                    personEmail,
                    phone,
                    billingStreet,
                    billingPostalCode,
                    billingCity,
                    billingState,
                    accountSource: mode === WARRANTY_MODES.CPSIA ? 'Cpsia' : 'Warranty',
                    accountCountryCode: country
                },
                productRegistration: {
                    purchaseDate,
                    serialNumber,
                    userInputOnlinePurchase: Boolean(userInputStoreUrl),
                    userInputOnlineStoreURL: userInputStoreUrl,
                    userInputProductName: userInputSelectedProductName,
                    userInputRetailerCity,
                    userInputRetailerName,
                    userInputRetailerCountry,
                    userInputSelectedProductGroup,
                    userInputSelectedProductName,
                    userInputWarrantyPeriodFromWeb: `${selectedItemConfig?.warranty}`,
                    description: '',
                    userInputItemNumber,
                    manufactureDate
                }
            }

            if (mode === WARRANTY_MODES.CPSIA) {
                body.productRegistration.productSafetyRegistration = true
            }

            logger.log('Submitting warranty registration', body)
            await submitWarrantyRegistration(
                {
                    body: JSON.stringify(body)
                },
                {
                    onError
                }
            )
        },
        [
            config?.warranties,
            country,
            locale,
            mode,
            preferredLanguage,
            submitWarrantyRegistration,
            site,
            onError
        ]
    )

    if (!isLoaded) {
        // short-circuit if the ocapi site is still loading
        // as otherwise we may emit false redirect and that prevents
        // followup rerenders when the ocapi site is loaded
        return null
    }

    // redirect to home if cpsia is not enabled for this site - using redirect also returns 302 on ssr request
    if (mode === WARRANTY_MODES.CPSIA && !cpsiaEnabled) {
        return <Redirect to={{pathname: buildUrl(HOME_HREF)}} />
    }

    // Check the customer type against the portal mode, cpsia - guest, warranty - registered
    // Using Redirect allows us to store the directed page to location
    // so we can direct users back after they are successfully log in
    if (customerType !== null && onClient()) {
        switch (mode) {
            case WARRANTY_MODES.CPSIA:
                // CPSIA mode allows guest and registered users
                break
            case WARRANTY_MODES.WARRANTY:
                if (!isRegistered) {
                    return (
                        <Redirect
                            to={{
                                // redirect to login
                                pathname: buildUrl(SIGN_IN_HREF),
                                // with return back to us on login
                                state: {directedFrom: `${location.pathname}?${location.search}`}
                            }}
                        />
                    )
                }
                break
            default:
                // throw in render mode for any other invalid mode
                throw new HTTPNotFound('Invalid mode')
        }
    }

    if (isLoading || isConfigLoading || (isRegistered && isCustomerLoading)) {
        return (
            // TODO: better loading state
            <Box>
                <LoadingSpinner />
                <Text>loading...</Text>
            </Box>
        )
    }

    return (
        <Box paddingBottom={10} position="relative">
            <Seo
                title={
                    mode === WARRANTY_MODES.CPSIA
                        ? formatMessage({
                              id: 'cpsia.product_registration.page_title',
                              defaultMessage: 'Product Registration'
                          })
                        : formatMessage({
                              id: 'warranty_portal.warranty_registration.page_title',
                              defaultMessage: 'Warranty Registration'
                          })
                }
            />
            <WarrantyProvider
                onSubmit={onSubmit}
                mode={mode}
                initialData={initialDataWithCustomerInfo}
                config={config}
            >
                <Container variant="newsLetterBanner">
                    <Container variant="newsLetterBannerInner">
                        <Box paddingX={4}>
                            {isSuccess ? (
                                <WarrantyConfirmation responseData={warrantyData} />
                            ) : (
                                <WarrantyWizard />
                            )}
                        </Box>
                    </Container>
                </Container>
            </WarrantyProvider>
            {isSubmitting && <LoadingSpinner />}
        </Box>
    )
}

WarrantyPortal.propTypes = {
    mode: PropTypes.oneOf(Object.values(WARRANTY_MODES)).isRequired,
    initialData: PropTypes.object
}

export default WarrantyPortal
